/**
 * Single source of truth for the standalone print document (Print button + PDF capture iframe).
 * Keeps layout/CSS identical between browser print and html2canvas PDF.
 */
export function buildBookingPrintDocumentHtml(printRootOuterHtml, documentTitle, headerBg) {
    return `<!DOCTYPE html>
<html>
<head>
  <title>Booking - ${documentTitle}</title>
  <meta charset="UTF-8" />
  <script src="https://cdn.tailwindcss.com"></script>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    @page {
      size: A4;
      margin: 8mm;
    }
    
    body {
      margin: 0;
      padding: 0;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      font-size: 10px;
      background: white;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    }
    
    .a4-page {
      width: 100%;
      min-height: 297mm;
      box-sizing: border-box;
      background: white;
    }

    .booking-print-table {
      width: 100%;
      border-collapse: collapse;
    }

    thead.booking-print-thead {
      display: table-header-group;
    }

    tfoot.booking-print-tfoot {
      display: table-footer-group;
    }

    @media print {
      .a4-page {
        min-height: 0 !important;
      }
    }
    
    /* Prevent section splits */
    .no-break {
      page-break-inside: avoid;
      break-inside: avoid;
    }
    
    /* Section header styling */
    .section-hdr {
      background-color: ${headerBg} !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    
    /* Table styles */
    table {
      border-collapse: collapse;
      width: 100%;
    }
    
    .border {
      border: 1px solid #9ca3af;
    }
    
    .border-t-0 {
      border-top: 0;
    }
    
    .border-l {
      border-left: 1px solid #9ca3af;
    }
    
    .border-r {
      border-right: 1px solid #9ca3af;
    }
    
    .border-b {
      border-bottom: 1px solid #9ca3af;
    }
    
    .border-gray-400 {
      border-color: #9ca3af;
    }
    
    .border-gray-300 {
      border-color: #d1d5db;
    }
    
    .divide-x > * + * {
      border-left: 1px solid #9ca3af;
    }
    
    .divide-gray-400 > * + * {
      border-left-color: #9ca3af;
    }
    
    .grid {
      display: grid;
    }
    
    .grid-cols-2 {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    
    .p-2 {
      padding: 0.5rem;
    }
    
    .px-3 {
      padding-left: 0.75rem;
      padding-right: 0.75rem;
    }
    
    .py-1 {
      padding-top: 0.25rem;
      padding-bottom: 0.25rem;
    }
    
    .text-xs {
      font-size: 0.75rem;
      line-height: 1rem;
    }
    
    .text-white {
      color: white;
    }
    
    .font-bold {
      font-weight: 700;
    }
    
    .uppercase {
      text-transform: uppercase;
    }
    
    .tracking-wide {
      letter-spacing: 0.025em;
    }
    
    .bg-white {
      background: white;
    }
    
    .text-gray-600 {
      color: #4b5563;
    }
    
    .text-gray-900 {
      color: #111827;
    }
    
    .font-medium {
      font-weight: 500;
    }
    
    .w-32 {
      width: 8rem;
    }
    
    .w-36 {
      width: 9rem;
    }
    
    .w-40 {
      width: 10rem;
    }
    
    .align-top {
      vertical-align: top;
    }
    
    .leading-snug {
      line-height: 1.375;
    }
    
    .leading-tight {
      line-height: 1.25;
    }
    
    .whitespace-pre-line {
      white-space: pre-line;
    }
    
    .shrink-0 {
      flex-shrink: 0;
    }
    
    .mr-2 {
      margin-right: 0.5rem;
    }
    
    .mb-1 {
      margin-bottom: 0.25rem;
    }
    
    .mt-1 {
      margin-top: 0.25rem;
    }
    
    .mt-2 {
      margin-top: 0.5rem;
    }
    
    .mt-3 {
      margin-top: 0.75rem;
    }
    
    .mb-2 {
      margin-bottom: 0.5rem;
    }
    
    .pt-2 {
      padding-top: 0.5rem;
    }
    
    .pt-3 {
      padding-top: 0.75rem;
    }
    
    .pb-2 {
      padding-bottom: 0.5rem;
    }
    
    .pb-3 {
      padding-bottom: 0.75rem;
    }
    
    .px-4 {
      padding-left: 1rem;
      padding-right: 1rem;
    }
    
    .pt-4 {
      padding-top: 1rem;
    }
    
    .flex {
      display: flex;
    }
    
    .justify-between {
      justify-content: space-between;
    }
    
    .items-start {
      align-items: flex-start;
    }
    
    .text-right {
      text-align: right;
    }
    
    .h-10 {
      height: 2.5rem;
    }
    
    .w-auto {
      width: auto;
    }
    
    .space-y-0 > * + * {
      margin-top: 0;
    }
    
    .border-t {
      border-top-width: 1px;
    }
    
    .border-t-2 {
      border-top-width: 2px;
    }
    
    .bg-blue-50 {
      background-color: #eff6ff;
    }
    
    .bg-green-50 {
      background-color: #f0fdf4;
    }
    
    .bg-gray-50 {
      background-color: #f9fafb;
    }
    
    .bg-gray-100 {
      background-color: #f3f4f6;
    }
    
    .text-green-800 {
      color: #166534;
    }
    
    .text-green-600 {
      color: #16a34a;
    }
    
    .text-gray-500 {
      color: #6b7280;
    }
    
    .text-gray-700 {
      color: #374151;
    }
    
    .border-green-200 {
      border-color: #bbf7d0;
    }
    
    .border-b {
      border-bottom-width: 1px;
    }
    
    .px-2 {
      padding-left: 0.5rem;
      padding-right: 0.5rem;
    }
    
    .py-0\\.5 {
      padding-top: 0.125rem;
      padding-bottom: 0.125rem;
    }
    
    .text-left {
      text-align: left;
    }
    
    .text-right {
      text-align: right;
    }
    
    .font-semibold {
      font-weight: 600;
    }
    
    .my-1 {
      margin-top: 0.25rem;
      margin-bottom: 0.25rem;
    }
    
    .gap-4 {
      gap: 1rem;
    }
    
    .ml-auto {
      margin-left: auto;
    }
    
    .w-44 {
      width: 11rem;
    }
    
    .h-5 {
      height: 1.25rem;
    }

    .booking-print-gutter {
      padding-left: 1rem;
      padding-right: 1rem;
      box-sizing: border-box;
    }

    .booking-print-header-address p {
      margin: 0;
      line-height: 1.25;
    }

    .booking-print-footer-inner {
      margin-top: 4px;
      padding-top: 4px;
      border-top: 1px solid #d1d5db;
      line-height: 1.25;
    }

    .booking-print-footer-grid {
      column-gap: 10px;
      row-gap: 0;
    }

    .booking-print-footer-stack {
      display: flex;
      flex-direction: column;
      gap: 3px;
    }

    .booking-print-footer-stack p {
      margin: 0;
      line-height: 1.25;
    }

    .booking-print-footer-sigline {
      height: 14px;
      margin-top: 2px;
      margin-bottom: 2px;
    }

    .booking-print-footer-note {
      margin-top: 6px;
      padding-top: 4px;
      border-top: 1px solid #e5e7eb;
    }

    .booking-print-footer-note p {
      margin: 0;
      line-height: 1.35;
    }
    
    .text-center {
      text-align: center;
    }
    
    .text-sm {
      font-size: 0.875rem;
      line-height: 1.25rem;
    }

    .table-fixed {
      table-layout: fixed;
    }

    .w-1\\/2 {
      width: 50%;
    }

    .w-full {
      width: 100%;
    }

    .border-collapse {
      border-collapse: collapse;
    }

    .justify-end {
      justify-content: flex-end;
    }

    .p-4 {
      padding: 1rem;
    }

    .border-none {
      border-style: none !important;
    }

    h3 {
      margin: 0;
    }
  </style>
</head>
<body>
  ${printRootOuterHtml}
</body>
</html>`;
}
